import { ApolloError } from '@apollo/client';
import {
  ChartColorContext,
  ChartDisclaimer,
  ChartErrorComponent,
  defaultEChartsOptionForInterval,
  ECHARTS_CONTAINER_HEIGHT as height,
  nowMarkLineSeriesOption,
} from '@e2m-customer-portal/domains';
import { useLocale } from '@e2m-customer-portal/hooks';
import { convertMeasure, isDefined, Nullable } from '@e2m-customer-portal/shared';
import { addHours } from 'date-fns';
import { EChartsOption, EChartsType, SeriesOption } from 'echarts';
import { Device, DeviceDatapoint, DeviceTimeSeries, Interval } from 'generated/graphql';
import { ReactElement, useContext, useRef } from 'react';
import EChartsReact, { EChartsReactRef } from 'react-echarts-library';
import { useTranslation } from 'react-i18next';
import { ChartColor, getSortedDeviceTimeSeries } from './utils';
import { Button } from '@mui/material';
import { ZoomOutIcon } from 'assets/icons';

function batteryDatapoints(
  datapoints: ReadonlyArray<DeviceDatapoint>,
  unit: string = 'kWh',
): Array<[number, Nullable<number>, Nullable<number>, Nullable<number>]> {
  return datapoints.map((datapoint) => [
    datapoint.date.getTime(),
    convertMeasure({ value: datapoint.energyCapabilityPos, unit: 'MWh' }, unit).value,
    convertMeasure({ value: datapoint.energyCapabilityNeg, unit: 'MWh' }, unit).value,
    datapoint.energyCapabilityPos != null && datapoint.energyCapabilityNeg != null
      ? (datapoint.energyCapabilityPos / (datapoint.energyCapabilityPos + datapoint.energyCapabilityNeg)) * 100
      : null,
  ]);
}

function noDataSeries(dataPoints: ReadonlyArray<DeviceDatapoint>) {
  return dataPoints.map((dataPoint, index) => [
    dataPoint.date.getTime(),
    index + 1 < dataPoints.length &&
    addHours(dataPoints[index + 1].date, 1).getTime() < Date.now() &&
    ((dataPoint.energyCapabilityPos == null && dataPoint.energyCapabilityNeg == null) ||
      ((dataPoint.energyCapabilityPos != null || dataPoint.energyCapabilityNeg != null) &&
        dataPoints[index + 1].energyCapabilityPos == null &&
        dataPoints[index + 1].energyCapabilityNeg == null))
      ? 100
      : 0,
  ]);
}

interface StateOfChargeChartProps {
  dataInterval: Interval;
  device: Device;
  deviceTimeSeries?: readonly DeviceTimeSeries[];
  deviceTimeSeriesError?: ApolloError;
  onChartReadyCallback: (instance: EChartsType) => void;
  resetZoom: (ref: EChartsReactRef) => void;
  isZoomed?: boolean;
}

export function StateOfChargeChart({
  dataInterval,
  device,
  deviceTimeSeries,
  deviceTimeSeriesError,
  onChartReadyCallback,
  resetZoom,
  isZoomed,
}: Readonly<StateOfChargeChartProps>): ReactElement<StateOfChargeChartProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const chartColorVariant = useContext(ChartColorContext);

  const sortedDatapoints = getSortedDeviceTimeSeries(device, deviceTimeSeries);
  const chartRef = useRef<EChartsReactRef>(null);

  const stateOfChargeSeries: SeriesOption = {
    data: batteryDatapoints(sortedDatapoints),
    type: 'line',
    symbol: 'circle',
    showSymbol: false,
    tooltip: {
      valueFormatter: (value) =>
        value == null ? '-' : `${(Math.round(Number(value) * 100) / 100).toLocaleString(locale)} kWh`,
    },
  };

  const noData = noDataSeries(sortedDatapoints);

  const option: EChartsOption = {
    ...defaultEChartsOptionForInterval(t, dataInterval),
    yAxis: [
      {
        type: 'value',
        name: t('MARKET_LOCATION_FEED_IN_CHART_ENERGY_CAPABILITY'),
        nameLocation: 'center',
        min: 0,
        max: 'dataMax',
        alignTicks: true,
        axisLabel: {
          formatter: (value) => `${Math.round(value)} kWh`,
          color: '#888888',
        },
        splitLine: {
          lineStyle: {
            color: '#F0F0F0',
          },
        },
      },
      {
        type: 'value',
        name: t('MARKET_LOCATION_FEED_IN_CHART_STATE_OF_CHARGE'),
        nameLocation: 'center',
        nameRotate: -90,
        min: 0,
        max: 100,
        axisLabel: {
          formatter: '{value} %',
        },
      },
    ],
    series: [
      {
        ...stateOfChargeSeries,
        name: t('MARKET_LOCATION_FEED_IN_CHART_ENERGY_CAPABILITY_POS'),
        color: ChartColor.Primary30[chartColorVariant],
        stack: 'charge',
        areaStyle: {},
      },
      {
        ...stateOfChargeSeries,
        name: t('MARKET_LOCATION_FEED_IN_CHART_ENERGY_CAPABILITY_NEG'),
        color: ChartColor.Primary60[chartColorVariant],
        stack: 'charge',
        areaStyle: {},
        encode: { y: 2 },
      },
      {
        ...stateOfChargeSeries,
        name: t('MARKET_LOCATION_FEED_IN_CHART_STATE_OF_CHARGE'),
        color: ChartColor.PrimaryFull[chartColorVariant],
        yAxisIndex: 1,
        encode: { y: 3 },
        tooltip: {
          valueFormatter: (value) =>
            value == null ? '-' : `${(Math.round(Number(value) * 100) / 100).toLocaleString(locale)} %`,
        },
      } satisfies SeriesOption,
      // Only show the series if there is any data missing at all (no name -> no legend)
      noData.some(([, point]) => point)
        ? ({
            data: noData,
            name: t('NO_DATA'),
            type: 'line',
            step: 'end',
            symbol: 'none',
            silent: true,
            showSymbol: false,
            yAxisIndex: 1,
            color: '#FF0A11',
            lineStyle: {
              width: 0,
            },
            areaStyle: {},
            tooltip: {
              show: false,
            },
            z: 0,
          } satisfies SeriesOption)
        : undefined,
      nowMarkLineSeriesOption,
    ].filter(isDefined),
  };

  return (
    <div className="relative">
      {isZoomed && (
        <Button
          className="absolute top-8 left-32 w-fit px-1 bg-white/45 z-50"
          style={{ color: ChartColor.PrimaryFull[chartColorVariant] }}
          color="inherit"
          variant="outlined"
          onClick={() => chartRef.current && resetZoom(chartRef.current)}
        >
          <ZoomOutIcon style={{ fill: ChartColor.PrimaryFull[chartColorVariant] }} />
        </Button>
      )}
      <EChartsReact
        style={{ height }}
        option={option}
        opts={{ locale, renderer: 'svg' }}
        data-testid="state-of-charge-chart"
        onChartReady={onChartReadyCallback}
        ref={chartRef}
      />
      {deviceTimeSeriesError && <ChartErrorComponent errors={[deviceTimeSeriesError]} />}

      <ChartDisclaimer
        title={t('COLOCATION_OVERVIEW_CHARTS_STATE_OF_CHARGE_DISCLAIMER_TITLE')}
        text={t('COLOCATION_OVERVIEW_CHARTS_STATE_OF_CHARGE_DISCLAIMER')}
      />
    </div>
  );
}
